import { QnHeaderIcon } from "@components/Layouts/QuickNews/QnButtons";
import { HamburgerMenuIcon } from "@header/HamburgerMenu/HamburgerMenuIcon";
import { HamburgerMenuModal } from "@header/HamburgerMenu/HamburgerMenuModel";
import { HeaderLogo } from "@header/HeaderLogo";
import { HeaderSubscribe } from "@header/HeaderSubscribe";
import { SearchButton } from "@header/Search/SearchButton";
import { SearchInput } from "@header/Search/SearchInput";
import { TopHeader } from "@header/TopHeader/TopHeader";
import { QUICKNEWS_FLAG } from "@helper/getEnvVariables";
import cx from "classnames";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

export type HeaderProps = {
  /**
   * Test ID used to identify the main container.
   * @default "header"
   */
  testId?: string;

  /**
   * Section name related to page.
   */
  sectionName?: string;

  /**
   * Current page scroll direction.
   */
  scrollDir?: string;

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;

  /**
   * Checks whether the search input is expanded.
   */
  isSearchExpanded: boolean;

  /**
   * Updates the search expanded state.
   */
  setIsSearchExpanded: (expanded: boolean) => void;
};

/**
 * Renders the main site header.
 *
 * Includes the top header, logo, subscription link, quick news,
 * search button, mobile hamburger menu, and search input.
 *
 * @param props - Header configuration and search state.
 * @returns The site header.
 */
export function Header({
  testId = "header",
  sectionName,
  scrollDir,
  isArticleFirstFoldEnabled = false,
  isSearchExpanded,
  setIsSearchExpanded,
}: HeaderProps) {
  const [showMenuIcon, setShowMenuIcon] = useState(true);
  const [expandMenu, setExpandMenu] = useState(false);

  const toggleMobileMenu = () => {
    setShowMenuIcon((prevShowMenuIcon) => !prevShowMenuIcon);
    setExpandMenu((prevExpandMenu) => !prevExpandMenu);
    (document.querySelector(".mobile-menu") as HTMLInputElement).setAttribute(
      "style",
      showMenuIcon ? "display: none;" : "display: block;",
    );
  };

  const toggleExpanded = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  return (
    <div
      className={twMerge(
        "top-0 z-50 relative max-lg:sticky bg-white-100 translate-y-0 transition-all duration-1000 ease-in-out",
        scrollDir === "down" ? "-translate-y-full" : "translate-y-0",
      )}
    >
      <TopHeader />
      <div
        data-testid={`${testId}-headerContainer`}
        className="z-0 lg:relative"
      >
        <div
          className={cx(
            "lg:relative bg-white-100 xl:max-w-xl xl:px-md lg:max-w-lg lg:p-0 lg:h-21 lg:mb-0 lg:px-sm lg:grid lg:grid-cols-6 md:max-w-full sm:max-w-full w-full mx-auto h-14 px-3.5 pt-3.5",
            sectionName === "maanavar-murasu" && "lg:h-24 lg:mt-2",
            isArticleFirstFoldEnabled && "lg:h-auto mt-0 pt-5 pb-2 lg:mt-380",
          )}
        >
          {QUICKNEWS_FLAG && (
            <div className="m-auto hidden lg:flex h-11 py-4xs items-start justify-start ml-0">
              <HeaderSubscribe />
            </div>
          )}
          <HeaderLogo
            testId={testId}
            sectionName={sectionName}
            isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
          />
          <div className="m-auto hidden lg:flex h-11 py-4xs mr-0">
            {QUICKNEWS_FLAG ? (
              <QnHeaderIcon />
            ) : (
              <div className="m-auto hidden lg:flex h-11 py-4xs items-start justify-start ml-0">
                <HeaderSubscribe />
              </div>
            )}

            <span
              className={`border-l border-solid border-gray-400 ${
                QUICKNEWS_FLAG ? "ml-2" : "ml-5"
              }`}
            />

            <SearchButton onClick={toggleExpanded} />
          </div>

          <div
            data-testid={`${testId}-hamburger-menu-container`}
            className="inline-flex float-right lg:hidden"
          >
            <QnHeaderIcon />
            <span className="border-l border-solid border-gray-400 mx-2" />
            <SearchButton onClick={toggleExpanded} />
            <span className="border-l border-solid border-gray-400 inline lg:hidden" />
            <HamburgerMenuIcon
              showMenuIcon={showMenuIcon}
              expandMenu={expandMenu}
              onClick={toggleMobileMenu}
            />
          </div>
        </div>

        <SearchInput
          expanded={isSearchExpanded}
          className={twMerge(
            "lg:max-w-xl mx-auto px-sm pb-4 bg-white-100",
            scrollDir === "down" && "max-lg:hidden",
          )}
        />

        <HamburgerMenuModal
          expandMenu={expandMenu}
          onClick={toggleMobileMenu}
        />
      </div>
    </div>
  );
}
